/* global window */
/**
 * @fileOverview common parsers which are common across sports.
 */

import selectn      from 'selectn'
import qs           from 'query-string'
import espnUrl      from 'espn-url'
import dateFormat   from '../../utils/dateformat'
import isReactPath  from '../../utils/isReactPath'
import parseUri     from '../../utils/parseURI'
import translate    from '../../utils/translate'
import helpers      from './cricket/gamepackage/helpers'
import { translateURI } from '../../utils/translateURI'
import { DATE_FORMATS } from '../../../constants/dateFormats'

const DD_MM = DATE_FORMATS.DD_MM
const H_MM_A_Z = DATE_FORMATS.H_MM_A_Z
const DD_MM_HH_MM_A_Z = DATE_FORMATS.DD_MM_HH_MM_A_Z
const URL_BLACKLIST_REGEX = '(espnfc|gamecast|watchespn|stubhub|insider|cricinfo|ncaa\.com|en\.espn)'

const common = {

  defaultUrls: {
    teamLogo : 'https://a.espncdn.com/combiner/i?img=/i/teamlogos/soccer/500/default-team-logo-500.png',
    playerLogo: 'https://a.espncdn.com/combiner/i?img=/i/headshots/cricket/players/default-player-logo-500.png'
  },

  buildURL: function (url = '', domain= '') {
    /**
     * If url is for espncricinfo.com, return it as is.
     * Else use url as returned by espn-url.
     * If an exception is thrown, return url as is
     */
    try{
      url = typeof url === 'string' ? url : ''
      domain = typeof domain === 'string' ? domain : ''

      const urlObj = parseUri(url) || {}

      const isEspnCricinfoSBUrl = (url.indexOf('espncricinfosb.com') > -1) ? true : false
      const isEspnUKUrl = (url.indexOf('en.espn.co.uk') > -1) ? true : false
      const isWatchEspnUrl = (url.indexOf('espn.com/watch') > -1) ? true : false
      const isEspnCricinfoUrl = (url.indexOf('espncricinfo.com') > -1) ? true : false
      const isEspnAUDomain = (domain.indexOf('espn.com.au') > -1) ? true : false
      const isEspnAfricaDomain = (domain.indexOf('africa.espn.com') > -1) ? true : false
      const isEspnCricinfoDomain = (domain.indexOf('espncricinfo.com') > -1) ? true : false
      const isEspnCricinfoLegacy = (url.indexOf('espncricinfo.com') > -1 && /(\/content\/|\/engine\/)/.test(url)) ? true : false
      const isTcmUrl = (url.indexOf('thecricketmonthly.com') > -1) ? true : false

      if ((isEspnCricinfoSBUrl || isEspnUKUrl || isWatchEspnUrl || isEspnCricinfoUrl) && !domain) {
        return url

      } else if(isEspnCricinfoLegacy || isTcmUrl) {
        return url
      } else if (!isEspnCricinfoUrl && isEspnCricinfoDomain) {
        let relativeUrl = urlObj.relative
        // remove `/cricket' for espncricinfo
        relativeUrl = relativeUrl.replace(/^\/cricket/, '')
        if(urlObj.protocol) {
          return `${urlObj.protocol}://${domain + relativeUrl}`
        } else {
          return relativeUrl
        }
      } else if (isEspnCricinfoUrl && isEspnCricinfoDomain) {
        return url
      } else if (isEspnCricinfoUrl && !isEspnCricinfoDomain) {
        let relativeUrl = urlObj.relative || {}
        // Add `/cricket' to cricket links for non-espncricinfo domains if /cricket is not in path
        if(relativeUrl.indexOf('/cricket/') === -1) {
          relativeUrl = relativeUrl.replace (/^/,'/cricket')
        }
        return relativeUrl
      } else if((isEspnAfricaDomain || isEspnAUDomain) && url.indexOf('/cricket') !== -1) {
        return parseUri(url).relative
      } else if(domain) {
        return espnUrl(url, domain)
      }
      else{
        return parseUri(url).relative
      }

    } catch(e) {
      return url
    }
  },

  /**
   * Creates href from params
   * @param  {String} options.pageType PageType
   * @param  {String} options.sport    Sport
   * @param  {String} options.leagueId League Id
   * @param  {String} options.gameId   Game Id
   * @param  {String} options.slug     Slug
   * @param  {String} options.webview  webview
   * @param  {String} options.platform  platform
   * @param  {Object} options.query    Query params
   * @return {String}                  Created href/url
   */
  createHref: function ({ pageType, sport, sortKey, sortDir, leagueId, league, season, gameId, slug, date, seasontype, query = {}, platform, webview, team, day, teamId, showSport = true }) {

    league = league || leagueId
    team = team || teamId

    let { filter, innings, ...rest } = query
    let href = `${ showSport ? '/' + sport : '' }/${pageType}`
    let queryObject = { ...rest }
    let leagueKey = 'league'

    if(sport === 'cricket') {
      leagueKey = 'series'
    }

    if( league == slug ) {
      slug = ''
    }

    switch(pageType) {
      case 'fixtures':
      case 'schedule':
      case 'results':
        if ( league || date || team || season ) {
          href += '/_'
        }
        href += `${team ? '/team/' + team : ''}${league ? '/'+ leagueKey +'/' + league : ''}${season ? '/season/' + season : ''}${date ? '/date/' + date : ''}${slug ? '/'+ slug : '' }`
        break

      case 'standings':
      case 'table':
        if ( sport !== 'cricket' ) {
          href += '/_'
          slug = ''
        }
        href += `${sortKey ? '/sort/' + sortKey : ''}${sortDir ? '/dir/' + sortDir : ''}${league ? '/'+ leagueKey +'/' + league : ''}${season ? '/season/' + season : ''}${seasontype ? '/seasontype/' + seasontype : ''}${slug ? '/'+ slug : '' }`
        break

      case 'scoreboard':
      case 'scores':
        if ( sport !== 'cricket' && league ) {
          href += '/_'
        }
        href += `${team ? '/team/' + team : ''}${league ? '/'+ leagueKey +'/' + league : ''}${season ? '/season/' + season : ''}${slug ? '/'+ slug : '' }`
        break

      case 'game':
      case 'match':
      case 'scorecard':
      case 'statistics':
      case 'matchstats':
      case 'playerstats':
      case 'lineups':
      case 'conversation':
      case 'coverage':
      case 'video':
      case 'hawkeye':
        if( sport === 'cricket' ) {
          // Appends a default slug 'webview' if the webview param exists.
          // This is necessary as the sendJSONMessage method replaces it
          // with the encoded JSON string, using window.location. This triggers a page reload
          // but keeps the user on the same page.
          href = `${ showSport ? '/' + sport : '' }/series/${league}/${pageType}/${gameId}${ slug ? '/' + slug : '' }${platform ? '/' + platform : '' }${webview ? '/' + 'webview' : '' }`
        } else if(sport === 'rugby' || sport === 'rugby-league') {
          href = `${ showSport ? '/' + sport : '' }/${pageType}${platform ? '/' + platform : '' }${webview ? '/webview' : '' }`
          queryObject = { gameId, ...rest, league:league }
        } else{
          href = `${ showSport ? '/' + sport : '' }/${pageType}`
          queryObject = { gameId, ...rest, league:league }
        }
        break
      case 'preview':
      case 'recap':
      case 'report':
        if(sport === 'cricket') {
          href = `${ showSport ? '/' + sport : '' }/series/${league}/${pageType}/${gameId}${ day ? ('/day/' + day) : '' }${ slug ? '/' + slug : '' }${platform ? '/' + platform : '' }${webview ? '/' + 'webview' : '' }`
        } else if(sport === 'rugby' || sport === 'rugby-league') {
          href = `${ showSport ? '/' + sport : '' }/${pageType}${platform ? '/' + platform : '' }${webview ? '/webview' : '' }`
          queryObject = { gameId, ...rest, league:league }
        } else {
          href = `${ showSport ? '/' + sport : '' }/${pageType}`
          queryObject = { gameId, ...rest, league:league }
        }
        break
      case 'commentary':
        if(sport === 'cricket') {
          href = `${ showSport ? '/' + sport : '' }/series/${league}/${pageType}/${gameId}${ slug ? '/' + slug : '' }${platform ? '/' + platform : '' }${webview ? '/' + 'webview' : '' }`
          queryObject = { filter, innings, ...rest }
        } else if(sport === 'rugby' || sport === 'rugby-league') {
          href = `${ showSport ? '/' + sport : '' }/${pageType}${platform ? '/' + platform : '' }${webview ? '/webview' : '' }`
          queryObject = { gameId, ...rest, league:league }
        }else{
          href = `${ showSport ? '/' + sport : '' }/${pageType}`
          queryObject = { gameId, ...rest, league:league }
        }
        break
      case 'team':
        href = `${ showSport ? '/' + sport : '' }/${pageType}${team ? '/_/id/' + team : ''}/${slug ? slug : ''}`
        break
    }

    let queryString = qs.stringify(queryObject)

    queryString = queryString ? '?' + queryString : ''

    href += queryString

    return href
  },
  /**
   * Function to clean up slug and convert it to lowercase
   * @param {String} slug will have the string to be filtered
   * @return {String} returns slug as a sting, lowercase, hypehnated
   */
  getSafeSlug(slug) {
    let cleanSlug = ''
    if(slug && (slug != '')) {
      cleanSlug = slug.replace(/\//g,'')
      cleanSlug = cleanSlug.replace(/\s+/g,'-').toLowerCase()
    }
    return cleanSlug
  },
  /**
   * [getSlugFromUrl Get the correct slug from URL, mostly used from API link]
   * @param  {String} offset [gameId or teamId etc which comes before the slug]
   * @param  {String} path   [path from the API or location from which slug has to be extracted]
   * @return {String}        [returns the slug, lowercase, hyphen separated, and remove extra slash]
   */
  getSlugFromUrl(offset, path) {
    let slug = ''
    let pathArr = path.split(offset)
    slug = (pathArr.length > 1) ? (pathArr && pathArr[1]) : ''
    if(slug && (slug != '')) {
      slug = slug.replace(/\//g,'')
      slug = slug.replace(/\s+/g,'-').toLowerCase()
    }
    return slug
  },
  /**
   * [createCanonical Create a normalized canonical which is unique, which has a single slug]
   * @param  {Object} edition    [editions data]
   * @param  {Object} params     [normalized route params]
   * @param  {Object} hrefConfig [hrefConfig params for creating advanced links]
   * @param  {String} newSlug    [new slug which will be unique from API]
   * @return {String}            [returns relative canonical url]
   */
  createCanonical(edition={}, params={}, hrefConfig={}, newSlug='') {
    //clone the params, except the slug
    let cloneParams = Object.assign({}, params)
    let cloneHrefConfig = Object.assign({}, hrefConfig)
    let canonical = ''
    cloneParams.slug = newSlug

    cloneHrefConfig.showSport = true

    if(edition.site === 'espncricinfo') {
      cloneHrefConfig.showSport = false
    }

    if(params.sport === 'cricket' && edition.site === 'espn') {
      cloneHrefConfig.showSport = false
    }
    //remove platform and webview params for keeping correct sharing URL in sapp
    if(cloneParams.platform) {
      cloneParams.platform = ''
      cloneParams.webview = ''
    }

    //create canonical url from params and hrefConfig and add new slug
    canonical = common.createHref({ ...cloneParams, ...cloneHrefConfig })
    return canonical

  },

  createTrackingName:function (trackingParams=[]) {
    let trackingName = ''
    trackingName=`&lpos=${trackingParams.join(':')}`
    return trackingName
  },

  /**
  * Function to build a structured object that AnchorLink will accept as {...link}
  * based around the common.createHref function, If used to create every link it will
  * represent a singular place to modify and validate URLS.
  * @param    {Object}  noname    Object of params none are required all will default
  * @return   {Object}            Link Object
  **/
  buildLinkObject: function ({
    params      = {},
    query       = {},
    edition     = {},
    text        = null,
    external    = false,
    dataRoute   = false,
    href        = '',
    tracking    = [],
    classes     = [],
    hrefConfig
  }) {

    let routerLink  = false

    if ( params.pageType && !href ) {
      href    = common.createHref({ query, ...params, ...hrefConfig }) || ''

      if(edition.language === 'es' || edition.language === 'pt') {
        //Translate URLs
        let localizedHref = translateURI(edition, { url: href }, false)
        href = localizedHref.url
      }
    }

    if ( !href ) {
      href = '#'
    }

    if( external || dataRoute ) {
      dataRoute = false
    } else if( !href.match( URL_BLACKLIST_REGEX ) ) {

      const parsedURI     = parseUri( href ) || {}
      const relativePath  = parsedURI.path || ''
      let validatePath    = relativePath

      if ( parsedURI.host ) {
        validatePath = 'http://' + edition.domain + relativePath
      }

      routerLink = isReactPath( edition.host, validatePath, edition )

      if( routerLink ) {
        href = relativePath
        if( parsedURI.query ) {
          href += '?' + parsedURI.query
        }
      }
    }

    if (classes && classes.constructor !== Array) {
      console.warn('classes expects an array')
      let tmpArr = []
      tmpArr.push( classes )
      classes = tmpArr
    }

    if (tracking && tracking.constructor !== Array) {
      console.warn('classes expects an array')
      let tmpArr = []
      tmpArr.push( tracking )
      tracking = tmpArr
    }

    return {
      text:           text || '',
      pageType :      params.pageType,
      to :            href,
      href :          href, /* backward compatability */
      trackingName :  common.createTrackingName( tracking ) || '',
      external :      external,
      isRouterLink :  routerLink,
      dataRoute :     dataRoute,
      classes :       classes,
      league: params.league
    }

  },

  /**
   * filter and order stats by names and pageType
   * @param  {Array}  stats stats node
   * @param  {Object} names stats to filter, ex:{ name: '', index: 0 }
   * @param  {String} pageType name of the page
   * @return {Array}       [description]
   */
  filterStatsByNames: function (stats = [], names = {}) {
    let extractedStats = []
    let filteredStat = {}
    for (let i = 0; i < stats.length; i++) {
      const currentStat = stats[i] || {}

      filteredStat = names[currentStat.name]
      if(filteredStat) {
        const position = typeof filteredStat.index === 'number' ? filteredStat.index : extractedStats.length
        // make default maiden value to 0 if api returns blank/empty
        if(currentStat.name === 'maidens') {
          currentStat.displayValue = currentStat.displayValue || '0'
        }
        extractedStats[ position ] = {
          name: currentStat.name,
          displayName: currentStat.displayName,
          abbreviation: currentStat.abbreviation,
          text: filteredStat.text,
          value: currentStat.displayValue ? currentStat.displayValue : '-'
        }

      }
    }

    return extractedStats
  },

  getCountryFromGeoLocation: function (geoLocation = {}) {
    var country = (selectn('country.isoCodeAlpha2', geoLocation) || '').toUpperCase()
    return {
      country
    }
  },

  /**
   * Convert detailed stats array to an object for easier lookup. Keys are the
   * stat name (stat.name)
   * @param  {Array}  stats Detailed stats from statistics node
   * @return {Object}       keys are stat name, value is an object in array
   */
  createStatsMap: function (stats = []) {
    let namedStats = {}
    for ( var i = 0; i < stats.length; i++ ) {
      let stat = stats[i]
      if ( stat.hasOwnProperty('name') === true) {
        namedStats[stat.name] = stat
      }
    }
    return namedStats
  },

  /**
   * get player by id
   * @param  {Array}  rosters Rosters node
   * @param  {String} id      Player id
   * @return {Object|null}         [description]
   */
  getPlayerById: function (rosters = [], id) {
    for ( let i = 0; i < rosters.length; i++ ) {
      const roster = rosters[i]
      const athleteId = selectn('athlete.id', roster)
      if(athleteId == id) {
        return roster
      }
    }
    return null
  },

  /**
   * get multiple players by ids
   * @param  {Array} rosters  Rosters node
   * @param  {Array} ids      Player ids to filter
   * @return {Object}         Players w/ their ids as keys
   */
  getPlayersByIds: function (rosters = [], ids = []) {
    let players = {}
    for (let i = 0; i < rosters.length; i++) {
      const roster = rosters[i]
      const id = selectn('athlete.id', roster)
      if(ids.indexOf(id) !== -1) {
        players[id] = roster
      }
    }
    return players
  },

  /**
   * get leaders
   * @param  {Array}  leadersNode leaders node
   * @param  {Object}  roleMap    maps role to text
   * @param  {Object}  statMap    maps stats to text
   * @param  {Object}  homeTeamId id of hometeam
   * @return {Object}             leaders categorized by roles
   */
  getLeaders: function (leadersNode = [], roleMap = {}, statMap = {}, homeTeamId = '') {
    let roles = {}
    leadersNode.map((t = {}) => {
      const teamId = selectn('team.id', t) || ''
      const leaders = t.leaders || []
      if(leaders.length === 0) {
        roles.tbdTeam = selectn('team.abbreviation', t) || ''
      }

      leaders.map((leaderRole = {}) => {
        let teamAbbr = selectn('team.abbreviation', t) || ''
        const roleName = roleMap[leaderRole.name] || leaderRole.name
        const leaders = leaderRole.leaders || []
        const statMapForRole = statMap[leaderRole.name]
        let playersByRole = []

        leaders.slice(0,2).map((leader) => {
          let headshot = selectn('athlete.headshot.href', leader) || ''
          headshot = headshot ? common.resizeCombinerLogo(headshot, { w:108, h:108, transparent:true, scale:'crop'  } ) : ''
          const statistics = leader.statistics || []
          const stats = statMapForRole ? common.filterStatsByNames(statistics, statMap[leaderRole.name]) : statistics
          roles[roleName] = roles[roleName] || {}
          roles[roleName].players = roles[roleName].players || []
          const athleteLinkText = helpers.getAthleteLinkText(leader.athlete)
          playersByRole.push({
            name: selectn('athlete.displayName', leader),
            team: teamAbbr,
            href: common.extractUrl(leader.athlete, athleteLinkText, 'desktop'),
            stats: stats,
            img: headshot
          })
        })

        /*
         * prepend to roles[roleName].players only
         * if homeTeamId available and matching with team id
        */
        if (homeTeamId && homeTeamId == teamId) {
          roles[roleName].players.unshift(...playersByRole)
        }
        /*
         * else append to roles[roleName].players
         * if no homeTeamId or by default
         */
        else {
          roles[roleName].players.push(...playersByRole)
        }
      })
    })

    return roles
  },

  /**
   * get group from standings in which teams are playing
   * @param  {Object} standingsNode standings node
   * @param  {Object} teams         teams
   * @return {Object}               group
   */
  getGroupFromTeams: function (standingsNode = {}, teams = {}) {
    const children = standingsNode.children || []
    const homeId = selectn('home.id', teams)
    const awayId = selectn('away.id', teams)
    let group = {}

    children.map((c) => {
      const entries = selectn('standings.entries', c) || []
      entries.map((entry) => {
        const team = entry.team || {}
        if(team.id == homeId || team.id == awayId) {
          group = c
        }
      })
    })

    return group
  },
  /**
   * get standings data for col-c
   * @param  {Object} standings       node from summary API
   * @param  {Array}  columns         Columns to show
   * @param  {String} sport           sport
   * @param  {String} section         section of website shown on
   * @param {Object} edition edition information
   * @return {Object}                 standings data
   */
  getStandingsData: function ( standings = {}, columns = [], sport, section, edition={} ) {

    const children 	= standings.children || []
    const title 	  = standings.name || ''
    const uid 		  = standings.uid || ''
    const iChildLen = children.length || 0
    const iColLen 	= columns.length || 0
    const lang      = edition && edition.language
    // team links for rugby-league should be inactive
    const isTeamLinkActive    = common.getIsTeamLinkActive(sport, lang)
    let teams 		  = []
    let keys 		    = []
    for ( let a = 0; a < iChildLen; a++ ) {
      const entries 		  = selectn('standings.entries', children[a]) || []
      const iEntriesLen 	= entries.length || 0
      let groupedTeams	  = []

      for( let b = 0; b < iEntriesLen; b++ ) {
        const entry 	  = entries[b]
        const stats 	  = selectn('stats', entry) || []
        const iStatLen 	= stats.length
        let tableData 	= []
        let oStatCache 	= {}

        /* itterate and cache Stats */
        for ( let c = 0; c < iStatLen; c++ ) {
          oStatCache[stats[c].type] = stats[c]
        }

        for ( let d = 0; d < iColLen; d++ ) {
          const statType = columns[d]
          if ( typeof oStatCache[statType] !== 'undefined' ) {
            if ( a === 0 && b === 0 ) {
              keys.push({
                text    : oStatCache[statType].abbreviation,
                tooltip :	oStatCache[statType].description,
                type    : statType
              })
            }
            tableData.push( oStatCache[statType].displayValue || oStatCache[statType].value )
          } else {
            /* TODO langauage support */
            if ( statType === 'team' ) {
              if ( a === 0 && b === 0 ) {
                keys.push({
                  text    : translate(edition, 'Team'),
                  tooltip : translate(edition, 'Team'),
                  type    : 'team'
                })
              }
              tableData.push('')
            }
          }
        }
        let hData = {
          href        : common.getTeamUrl( entry.team ),
          abbrev      : selectn('team.abbreviation', entry) || '',
          displayName : selectn('team.displayName', entry) || '',
          stats       : tableData,
          trackingName: sport && section ? common.createTrackingName([ sport, section, 'Standings', 'Team' ]) : '',
          id          : entry.team.id
        }

        groupedTeams.push(hData)
      }

      teams.push({
        keys          : keys,
        teams         : groupedTeams,
        abbreviation  : children[a].name || children[a].abbreviation || ''
      })
    }

    return { title, uid, teams, isTeamLinkActive }
  },

  /**
   * Get standings link details
   * @param  {Object} params Router params
   * @param  {Object} query  Router query
   * @param  {Object} section  section name
   * @param  {Object} hrefConfig config object for create href function
   * @param  {Number} year  year of the league
   *  @param {Object} edition edition information
   * @return {Object}        link title and href
   */
  getStandingsLink(params = {}, query = {}, section = 'game', hrefConfig, year, edition={} ) {
    const leagueId = params.leagueId || params.league
    const link = {
      title: translate(edition, 'Full Table'),
      trackingName: common.createTrackingName([ params.sport, section , 'Standings', 'Full' ])
    }

    if(leagueId) {
      link.href = common.createHref({ query, ...params, pageType: 'table', ...hrefConfig, season: year })
    }

    return link
  },
  /**
   * get game summary
   * @param  {Object} competition Competition object
   * @return {String}             summary
   */
  getGameSummary: function (competition = {}) {
    let session = selectn('status.session', competition) || ''
    if(session) {
      session = session + ': '
    }
    const summary = session + selectn('status.summary', competition)
    return summary
  },

  /**
   * get Gamestrip data
   * @param  {Object} competition       Competition object
   * @param  {String} matchDescription  description of the match
   * @param  {String} gameState         game status
   * @param  {String} sport             Sport
   * @param  {String} timeZoneBucket    timeZoneBucket
   * @param  {Object} params            params object
   * @param  {String} domain            domain rel e.g. espn.in, espncricinfo.com etc
   * @param {Boolean} timeValid timeValid
   * @param {String} language language
   * @param {Object} editionDateConfig current edition date config
   * @return {Object}                   Gamestrip data
   */
  getGameStrip: function ( competition = {}, matchDescription = '', gameState, sport = 'cricket', timeZoneBucket, params = {}, domain = '', timeValid=true, language = 'en', editionDateConfig = {}) {

    // is game odi or test
    //const { limitedOvers } = competition
    // is game live
    const competitors = selectn('competitors', competition)
    const reducedOvers = selectn('reducedOvers', competition)
    const state       = selectn('status.type.state', competition)
    const isMobile    = selectn('mobile', params) || false
    const teams       = common.getTeams(competitors, sport, gameState, reducedOvers, state, isMobile, domain)
    const stateDesc   = selectn('status.type.description', competition)
    const matchTime   = selectn('status.displayClock', competition) || '-'
    const header      = matchDescription
    const network     = ''
    // team links for rugby-league should be inactive
    const isTeamLinkActive    = common.getIsTeamLinkActive(params.sport, language)
    const winner = common.getWinner(competitors)
    let winnerClass
    let winnerClassMobile
    let winnerClassFeature
    let date          = dateFormat.dateFormat( competition.date, editionDateConfig[DD_MM] || DD_MM, timeZoneBucket )
    let time          = dateFormat.dateFormat( competition.date, H_MM_A_Z, timeZoneBucket )
    let target        = ''
    let isoDate = competition.date
    let dateTimeFormat = editionDateConfig[DD_MM_HH_MM_A_Z] || DD_MM_HH_MM_A_Z
    if (winner.home === true) {
      winnerClass   = 'team-a-winner'
      winnerClassMobile  = 'cscore--home-winner'
      winnerClassFeature = 'cscore__home--winner'
    } else if (winner.away === true) {
      winnerClass   = 'team-b-winner'
      winnerClassMobile  = 'cscore--away-winner'
      winnerClassFeature = 'cscore__away--winner'
    }

    switch (state) {
      case 'pre':
        if ( sport === 'cricket' ) {
          date = timeValid ? dateFormat.dateFormat(isoDate, dateTimeFormat, timeZoneBucket) : ''
          time = ''
        }
        break
      case 'in':
        // If winner class is empty, check if target needs to be shown
        if ( winnerClass === '' && sport === 'cricket') {
          target = gameState === 'live' ? common.getTarget( competitors ) : ''
        }

        time = stateDesc

        if ( sport !== 'cricket' ) {
          time = matchTime
        }

        break
      case 'post':
        time = stateDesc
    }

    return { teams, header, network, date, time, winnerClass, winnerClassMobile, winnerClassFeature, target, gameState, isTeamLinkActive, isoDate, timeValid }
  },

  /**
    * [ getIsTeamLinkActive returns true/false based on the sport]
    * @param  {string} sport           [sport to check if active or inactive]
    * @param  {lang} lang           [SEWEB-13325 language es / pt has inactive teams temporarily for rugby ]
    * @return {boolen}                  [returns true /  false]
    */
  getIsTeamLinkActive: function (sport = '', lang = 'en') {
    // team links for rugby-league should return false (inactive)
    return ((sport === 'rugby-league') || (lang === 'es' || lang === 'pt')) ? false : true
  },

  /**
   * [getDetailsHeaderClass create details-header class for webview only]
   * @param  {String} matchDescription [if match note is present class will be set]
   * @param  {[type]} params           [params to check if platform is ios,android or windows]
   * @return {[type]}                  [returns class name details-header]
   */
  getDetailsHeaderClass: function ( matchDescription = '', params) {
    let platform = ''
    let detailsHeaderClass = ''
    if(params) {
      if(params.hasOwnProperty('platform') === true) {
        platform = params.platform
        if((matchDescription !== 'undefined') && (typeof platform !== 'undefined')) {
          detailsHeaderClass = 'details-header'
        }
      }
    }
    return detailsHeaderClass
  },

  /**
   * Extract the league name
   * @param  {Object} league league object
   * @return {String}        league name
   */
  getLeagueName: function (league = {}) {
    return league.name || ''
  },

  /**
   * get teams info
   * @param  {Array}  competitors Competitors array
   * @param  {String} sport       Sport
   * @param  {String} gameState   Game state
   * @param  {Boolean}  reducedOvers - True if match with reduced overs
   * @param  {String}  state of the match
   * @param  {Boolean}  isMobile - True if device is mobile
   * @param  {String} domain   domain rel e.g. espn.in, espncricinfo.com etc
   * @return {Object}             teams
   */
  getTeams: function (competitors = [], sport, gameState = '', reducedOvers, state, isMobile = false, domain = '') {
    let teams = {}
    const team1Name = selectn('[0].team.name', competitors)
    const team2Name = selectn('[1].team.name', competitors)
    if(competitors.length > 1 && (selectn('[0].homeAway', competitors) === selectn('[1].homeAway', competitors))) {
      competitors[0].homeAway = 'home'
      competitors[1].homeAway = 'away'
    }
    competitors.map((competitor, i) => {
      let teamId = selectn('team.id', competitor)
      let score = competitor.score || ''
      // split score on runs & overs
      let [ runDetails, overDetails ] = common.splitScore(score)
      if (typeof teamId !== 'undefined') {
        let homeAway = competitor.homeAway
        // If both teams are TBD, teams[homeAway] gets overwritten.
        // Update homeAway for 2nd TBD team
        if(team1Name === 'TBD' && team2Name === 'TBD' && i === 1) {
          homeAway = 'away'
        }
        teams[homeAway] = teams[homeAway] || {}
        teams[homeAway].id = competitor.team.id,
        teams[homeAway].name = competitor.team.name
        teams[homeAway].shortName = competitor.team.nickname
        teams[homeAway].abbrev = competitor.team.abbreviation
        // cache color and assign color to team
        let color = competitor.team.color
        teams[homeAway].color = color
        teams[homeAway].score = score
        teams[homeAway].record = gameState == 'pregame' ? common.getTeamForm( competitor ) : ''
        teams[homeAway].runDetails = runDetails
        teams[homeAway].overDetails = overDetails
        //because gameState is live for 2 hours after game is over, passing actual state
        let actualState = common.getGameState(state)
        let isTeamBattingNow = helpers.isTeamBattingNow(competitor, actualState)

        // show target markup for live matches and reduced overs post matches.
        let showTarget = (gameState === 'live' || (gameState === 'final' && reducedOvers)) ? true : false
        teams[homeAway].scoreMarkup = common.getFullScoreMarkup(score, 'over', 'span', false, false, showTarget, isMobile)
        teams[homeAway].scoreMarkupMobile = common.getFullScoreMarkup(score, 'cscore_overs', 'span', true, isTeamBattingNow, showTarget, isMobile)
        teams[homeAway].href = common.getTeamUrl(competitor.team, '', '', domain, sport)
        teams[homeAway].logo = common.getTeamLogo( competitor.team, { h:42 , w:42 } )
        teams[homeAway].bannerLogo = common.getTeamLogo( competitor.team, { h:288 , w:288 } )
        // Set winner and flag to check if this team batted first
        teams[homeAway].winner = competitor.winner
        teams[homeAway].battedFirst = selectn('linescores[0].isBatting', competitor)
        teams[homeAway].order = competitor.order || 0
        // Reset color if it's not available in feed based on who batted first
        if (typeof color === 'undefined' || color === '') {
          teams[homeAway].color = teams[homeAway].battedFirst ? '#40b5e5' : '#ef3a49'
        }
        if(sport) {
          teams[homeAway].trackingName = common.createTrackingName([ sport,'game','game','clubhouse','team' ])
        }
        teams[homeAway].uid = selectn('team.uid',competitor)
      }
    })
    return teams
  },

  getTeamForm: function ( competitor = {} ) {
    const teamForm = selectn( 'form', competitor ) || ''
    if ( teamForm ) {
      return 'Form: ' + teamForm
    }
    return ''
  },

  /**
   * get the target for chasing team
   * @param  {Array}  competitors - all teams
   * @return {String}             - target score
   */
  getTarget: function (competitors = []) {
    let target = ''

    competitors.map((c) => {
      // return if target already constructed
      if (target !== '') {
        return
      }

      (c.linescores || []).map((ls) => {
        // get target of batting team
        if (ls.isCurrent === 1 && ls.isBatting) {
          // Display target only when it's a non zero value
          if (ls.target !== 0) {
            target = `TARGET: ${ls.target}`
          }
        }
      })
    })

    return target
  },

  /**
   * construct the score for an innings of a team in header
   * @param  {Object}  competitor    - any team
   * @param  {Boolean}  limitedOvers - type of match
   * @param  {Boolean} isLive        - is match live
   * @return {String}                - score string
   */
  getFullScore: function (competitor = {}, limitedOvers, isLive) {
    const { linescores = [] } = competitor
    const delimiter = ' '
    let score = ''
    let statsByInn = []
    // let totalBatInn

    // populate the stats by innings
    linescores.map((ls) => {
      let statsNode
      let stats = {
        all : {},
        brief: {}
      }

      if (!ls.isBatting) {
        return
      }

      // get stats node
      statsNode = selectn('statistics.categories[0].stats', ls) || ''

      // get all stats mapped
      stats.all = statsNode ? common.createStatsMap(statsNode) : {}

      // get brief stats
      for (var k in ls) {
        let v = ls[k]
        // Skip any object value
        if (v instanceof Object === false) {
          stats.brief[k] = v
        }
      }

      // append to stats list
      statsByInn.push(stats)
    })

    // total number of batting innings
    // totalBatInn = statsByInn.length

    // construct the score string
    statsByInn.map((stats, index) => {
      const briefStats = stats.brief
      //console.info('stats', stats)

      // set flag check if team is playing live now
      const isPlayingNow = (isLive && briefStats.isBatting && briefStats.isCurrent === 1) ? true : false

      // over limit from stats
      const overLimit = selectn('all.overLimit.displayValue', stats) || ''
      //console.info('overLimit', overLimit)

      // set flag check if batting at last innings
      //const isAllInnEnd = totalBatInn === (index + 1) ? true : false

      // overs played
      let overs = ''

      // added a break for consecutive innings (not 1st inn)
      if (index !== 0) {
        score += `${delimiter}&${delimiter}<br>`
      }

      // append the score
      score += briefStats.runs
      /**
       * Check if it's All out - # wickets is 10 or description is all out
       * There might be a case of because of injuries #wickets is 9
       * and yet, it is all out. So do not reply on just the #wickets.
       */
      let isAllOut = selectn('brief.wickets', stats) == 10 ||
                     selectn('brief.description', stats) == 'all out'

      // append wickets for all cases where it not all out
      if (!isAllOut) {
        score += `/${briefStats.wickets}`
      }

      // append marker if team is playing live
      if (isPlayingNow) {
        score += '*'
      }

      // append marker if inn is 'declared'
      if (briefStats.description === 'declared') {
        score += 'd'
      }

      // append marker if inn is 'follow on'
      if (briefStats.followOn === 1) {
        score += `${delimiter}(f/o)`
      }

      // append overs if winner at last inn or team is playing live
      if (limitedOvers) {

        // construct overs for odi or test
        if (overLimit) {
          overs = `${briefStats.overs}/${overLimit}`
        } else {
          overs = briefStats.overs
        }

        score += `${delimiter}<span class="over">(${overs} ov)</span>`
      }
    })

    return score
  },

  /**
   * get game officials
   * @param  {Array}  officials gameInfo.officials
   * @param {Object} params which will page info
   * @return {Object}           Grouped Officials object
   */
  getOfficials: function (officials = [], params={}) {
    let officialsObj = {}
    officials.map((o, i) => {
      const position = selectn('position.displayName', o) || i
      const oimg = selectn('flag.href', o) || ''
      const oImgAlt = selectn('flag.alt', o) || ''
      officialsObj[position] = officialsObj[position] || []
      officialsObj[position].push({
        name: o.displayName,
        img: oimg,
        href: selectn('links.href', o) || '#',
        imgicon: common.resizeCombinerLogo(oimg, { w:25, h:25 } ),
        imgAlt: oImgAlt,
        trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'player' ])
      })
    })

    return officialsObj
  },

  /**
   * Get man of the match or man of the series
   * @param  {Array}  featuredAthletes node from the feed
   * @param  {String} award            displayName property value ("Player Of The Match")
   * @param  {Object} params           router params
   * @return {Array}                   list of player details (name, img, href)
   */
  getAwardedPlayer: function (featuredAthletes = [], award = '', params={}) {
    // default return value
    let players = []
    // Iterate over all awarded players and pick up player details
    for (let i = 0; i < featuredAthletes.length; i++) {
      let p = featuredAthletes[i] || {}
      if (p.displayName === award) {
        const pimg = selectn('team.logos[0].href', p) || ''
        let headshot = selectn('athlete.headshot.href', p) || ''
        headshot = headshot ? common.resizeCombinerLogo(headshot, { w:168, h:168, transparent:true, scale:'crop'  } ) : ''
        let awardedPlayer = {
          id: selectn('playerId', p) || '',
          name:  selectn('athlete.name', p) || '',
          displayName: selectn('athlete.displayName', p) || '',
          shortName: selectn('athlete.shortName', p) || '',
          headshot: headshot,
          img: pimg,
          imgicon: common.resizeCombinerLogo(pimg, { w:25, h:25 } ),
          href: selectn('athlete.links[0].href', p) || '#',
          team: selectn('team.name', p) || '',
          trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'player' ])
        }
        players.push(awardedPlayer)
      }
    }
    return players
  },


  /**
   * Get game details
   * @param  {Object} game info
   * @param  {Array} notes Notes
   * @param  {Array} featuredAthletes man of the match and man of the series
   * @param {Object} params to hold page info
   * @return {Object}             game details
   */
  getGameDetails: function (game, notes, featuredAthletes, params) {
    const venue = common.getVenueName(game)
    const venueLink = common.getVenueLink(game)
    // TODO: replace defaults
    const temperature = common.getTemperature(game)
    const weatherClass = common.getClassNameForSkyCode(game)
    const location = common.getLocation(game)
    const officials = common.getOfficials(game.officials, params)
    const playerOfMatch = common.getAwardedPlayer(featuredAthletes, 'Player Of The Match', params)
    const playerOfSeries = common.getAwardedPlayer(featuredAthletes, 'Player Of The Series', params)
    const featuredAthletesDetails = common.getFeaturedAthletes(featuredAthletes, params)
    const otherNotes = common.getNotesWithLink(notes,[ 'hoursofplay', 'toss', 'seriesnote', 'matchnumber', 'season', 'matchdays', 'points', 'ballsperover', 'playersperside' ])
    const { hoursofplay: hoursOfPlay, toss, seriesnote: seriesResult, matchnumber: matchNo, season,
      matchdays: matchDays, points, ballsperover:ballsPerOver, playersperside  } = otherNotes
    const playerReplacement = common.getPlayerReplacement(notes)

    return { venue, venueLink, temperature, weatherClass, location, officials, hoursOfPlay, toss, playerOfMatch, playerOfSeries, seriesResult, matchNo, season, matchDays, points, ballsPerOver, playersperside, featuredAthletesDetails, playerReplacement }
  },

  /**
   * Get featured athletes for match details
   * @param  {Array}  featuredAthletes node from the feed
   * @param  {Object} params           router params
   * @return {Array}                   list of featured athletes details (name, img, href)
   */
  getFeaturedAthletes: function (featuredAthletes = [], params={}) {
    // default return value
    let athletesTypes = []
    featuredAthletes.map((featuredAthlete = []) => {
      const athleteId = selectn('playerId', featuredAthlete) || ''
      if(typeof athleteId !== 'undefined' && athleteId !== '') {
        let featuredAthleteName = featuredAthlete.name || ''
        const pimg = selectn('team.logos[0].href', featuredAthlete) || ''
        let featuredPlayer = {
          id: athleteId,
          name:  selectn('athlete.name', featuredAthlete) || '',
          displayName: selectn('athlete.displayName', featuredAthlete) || '',
          shortName: selectn('athlete.shortName', featuredAthlete) || '',
          img: pimg,
          imgicon: common.resizeCombinerLogo(pimg, { w:25, h:25 } ),
          href: selectn('athlete.links[0].href', featuredAthlete) || '#',
          team: selectn('team.name', featuredAthlete) || '',
          trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'player' ]),
          featuredAthletesType: featuredAthlete.displayName,
          featuredAthletesLabel: featuredAthleteName
        }
        athletesTypes[featuredAthleteName] && athletesTypes[featuredAthleteName].push(featuredPlayer) || (athletesTypes[featuredAthleteName] = [ featuredPlayer ])
      }
    })
    athletesTypes = Object.entries(athletesTypes) || {}
    return athletesTypes
  },

  /**
   * Get player replacement for match details
   * @param  {Array}  notesNode notes from the feed
   * @return {Array}                   player replacement details
   */
  getPlayerReplacement(notesNode = []) {
    let playerReplacement = []

    for (let i = notesNode.length - 1; i >= 0; i--) {
      const note = notesNode[i] || {}
      if (note.type === 'playerreplacement') {
        const items = note.items

        items.map( (item = {}) => {
          playerReplacement.push({
            text: item.teamName + ' replacement',
            links: item.text || []
          })
        })
        break
      }
    }

    return playerReplacement
  },

  /**
   * get game videos
   * @param  {Array}  videos data.videos
   * @return {Array}  parsed videos
   */
  getVideos: function (videos = []) {
    // Handle error, if the API returns an Object instead of on Array
    videos = (!videos.length) ? [] : videos
    return videos.map(common.getVideo)
  },

  /**
   * get game video
   * @param  {Object} video parsed video
   * @return {Object}  parsed video
   */
  getVideo: function (video = {}) {
    const id = video.id || ''
    const cerebroId = video.cerebroId || ''
    const headline = video.headline || ''
    const description = video.description || ''
    const source = video.source || ''
    const ad = video.ad || {}
    const thumbnail = video.thumbnail || ''
    const coverageType =  selectn('tracking.coverageType',video) || ''
    const tracking =  selectn('tracking',video)
    const deviceRestrictions =  selectn('deviceRestrictions',video)
    const geoRestrictions = selectn('geoRestrictions', video)
    const links =  selectn('links',video)
    const duration = video.duration || -1 // As per App requirment
    return { id, cerebroId, headline, ad, description, source, thumbnail, coverageType, tracking, deviceRestrictions, geoRestrictions, links, duration }
  },

  /**
   * get Note by type
   * @param  {Array} notes Notes
   * @param  {String} type Note type
   * @return {String}      Note
   */
  getNote: function (notes = [], type = '') {
    let note = ''

    notes.map((n) => {
      if(n.type === type && n.text) {
        note = n.text
      }
    })
    return note
  },
  /**
   * get Note and hyperlink by type
   * @param {Array} notes Notes
   * @param {Array} types Notes type
   * @return {Object} notesObj Notes object
   */
  getNotesWithLink: function ( notes =[], types = [] ) {
    if(!(notes.length && types.length)) {
      return {}
    }
    let notesObj = {}
    for( let i=0; i<notes.length; i++) {
      let type = notes[i].type
      if(typeof type !== 'undefined' && types.indexOf(type) !== -1) {
        if(notes[i].href) {
          notesObj[type] = {
            name : notes[i].text || '',
            href : notes[i].href || '',
            trackingName:common.createTrackingName([ 'cricket', 'game', 'game', 'matchDetails' ])
          }
        }
        else {
          notesObj[type] = notes[i].text || ''
        }
      }
    }
    return notesObj
  },
  /**
   * Get venue location summary
   * @param  {Object} game details
   * @return {String}             venue location
   */
  getLocation: function (game) {
    const venue = game.venue || {}
    const address = venue.address || {}
    const summary = address.summary || ''
    const shortName = venue.shortName || ''
    const city = address.city || ''
    return { summary, shortName, city }
  },

  /**
   * Get venue name
   * @param  {Object} game details
   * @return {String}             venue if found else empty string
   */
  getVenueName: function (game) {
    return selectn('venue.fullName', game) || ''
  },

  /**
   * Get venue link
   * @param  {Object} game details
   * @return {String}             venue link if found else empty string
   */
  getVenueLink: function (game) {
    return selectn('venue.links[0].href', game) || ''
  },

  /**
   * Is venue information present
   * @param  {Object}  game details
   * @return {Boolean}             true if venue is found else false
   */
  isVenuePresent: function (game) {
    return common.getVenueName(game) != '' ? true : false
  },

  /**
   * Is weather information present for this game
   * @param  {Object}  game Event object
   * @return {Boolean}    true if weather info is found else false
   */
  isWeatherInfoPresent: function (game) {
    return typeof selectn('weather', game) !== 'undefined' ? true : false
  },


  /**
   * Get temperature at the venue
   * @param  {Object} game Event Object
   * @return {String}    Temperature if present else empty string
   */
  getTemperature: function (game) {
    let temperature = selectn('weather.temperature', game)
    // Convert to Celcius only if temp is not undefined or empty string
    return (typeof temperature !== 'undefined' && temperature !== '') ? common.convertToCelsius(temperature) : ''
  },

  convertToCelsius: function (value) {
    return Math.round((value - 32) * (5 / 9))
  },

  /**
   * Get class name for the weather cloud
   * @param  {Object} game Event object
   * @return {String}    class name for the weather cloud
   */
  getClassNameForSkyCode: function (game) {
    // Get sky image weather code
    let skycode = selectn('weather.skyImage.alt', game)
    // Default class name string
    let classname = ''

    /*
    // Classname for which an icon font class exists
    let validClassNames = [
      'sun', 'cloudy', 'partly-cloudy', 'fog', 'rain',
      'snow', 'sleet', 'night-clear', 'night-cloudy'
    ]
    */

    /**
     * Map to convert the sky code text string to icon font classes
     * workaround since the sky code is not the same as the toolkit classes
     * @type {Object}
     */
    let skycodeToIconfont = {
      'Sunny' : 'sun',
      'Mostly Sunny': 'sun',
      'Partly Cloudy/Wind' : 'partly-cloudy',
      'Partly Cloudy' : 'partly-cloudy',
      'AM Clouds/PM Sun' : 'partly-cloudy',
      'Mostly Cloudy' : 'cloudy',
      'Rain/Wind' : 'rain',
      'Showers Early' : 'rain',
      'Showers' : 'rain',
      'PM Showers' : 'rain',
      'Rain' : 'rain',
      'Light Rain': 'rain',
      'AM Rain/Wind' : 'rain',
      'AM Light Rain/Wind' : 'rain',
      'AM Light Rain' : 'rain'
    }
    //console.info('skycode, found', skycode, skycode in skycodeToIconfont)

    // Return if skycode is found in skycodeToIconfont
    if (skycode in skycodeToIconfont) {
      return skycodeToIconfont[skycode]
    }
    // Default empty string if not found
    return classname
  },


  /**
   * Resize if an image is on CDN and resize as per passed opts. If url is not
   * from CDN, return image URL as is.
   * Copied word for word from score - global scorecard main.js
   *
   * @param  {String} href url of the image
   * @param  {Object} opts options for the required image
   * @return {String}      image URL
   *
   * @example
   * resizeCombinerLogo(imageUrl, { w:35, h:35 })
   */
  resizeCombinerLogo: function (href = '', opts = {}) {
    let baseHeight = opts && opts.h || 25
    let baseWidth = opts && opts.w || 25
    let dpr = opts.dpr || (typeof window !== 'undefined') ? window.devicePixelRatio : 1
    let scale = opts.scale
    let transparent = opts.transparent
    let newW
    let newH

    let widthRegex = /&w=(\d)+/
    let heightRegex = /&h=(\d)+/

    if (dpr > 3) {
      dpr = 3
    }

    if (href) {

      // convert non-combiner cdn urls
      if (href.indexOf('/combiner/') === -1) {
        href = href.replace(/a(?:\d|ssets)?\.(?:espncdn|espn\.go)\.com\/i\//i, function () {
          return 'a1.espncdn.com/combiner/i?img=/i/'
        })
      }

      // only proceed if we have a combiner url
      if (href.indexOf('/combiner/') === -1) {
        return href
      }

      if (opts.h !== false) {
        newH = '&h=' + Math.ceil(baseHeight * dpr)
        if (href.match(heightRegex)) {
          href = href.replace(heightRegex, newH)
        } else {
          href += newH
        }
      }

      if (opts.w !== false) {
        newW = '&w=' + Math.ceil(baseWidth * dpr)
        if (href.match(widthRegex)) {
          href = href.replace(widthRegex, newW)
        } else {
          href += newW
        }
      }

      if (scale) {
        href += '&scale=' + scale
      }

      if (transparent) {
        href += '&transparent=true'
      }
    }

    return href
  },

  /**
   * convert the image URL into combiner image URL
   * @param  {String} href image URL
   * @return {String}      combiner image URL
   */
  getCombinerImage: function (href = '') {
    // return unmodified if href empty or combiner URL
    if (href === '' || href.indexOf('/combiner/') !== -1) {
      return href
    }

    return href.replace(/a(?:\d|ssets)?\.(?:espncdn|espn\.go)\.com\//i, function () {
      return 'a1.espncdn.com/combiner/i?img=/'
    })
  },

  /**
  * Get team's logo if available else return default team logo
  * @param  {Object}  team  team object from feed
  * @param  {Object}  opts  options for the required image
  * @return {String}        url of the team logo
  */
  getTeamLogo: function ( team = {}, opts = {} ) {

    let combinerLogo = ''

    if(team.logos && team.logos.length > 0) {
      combinerLogo = selectn('logos[0].href', team) || ''
    }else if(team.logo) {
      combinerLogo = team.logo
    }

    let combinerOpts = {
      w: opts.w || 35,
      h: opts.h || 35
    }

    return common.resizeCombinerLogo( combinerLogo || common.defaultUrls.teamLogo, combinerOpts )

  },

  /**
   * Get team Url
   * @param  {Object} team      team object from feed
   * @param  {String} linkText  link text for which link needs to be extracted
   * @param  {String} device    link rel e.g. desktop, mobile etc
   * @param  {String} domain    domain rel e.g. espn.in, espncricinfo.com etc
   * @param  {String} sport     sports e.g. cricket, rugby etc
   * @return {String}      team URL
   */
  getTeamUrl: function (team = {}, linkText, device, domain, sport = '') {
    // Default to Clubhouse link if falsy value is passed to linkText
    if (!linkText) {
      linkText = 'Clubhouse'
    }
    // Default to desktop link if falsy value is passed to device
    if (!device) {
      device = 'desktop'
    }
    // Get link whose text is Clubhouse and rel contains desktop
    return common.extractUrl(team, linkText, device, domain, sport )
  },

  /**
   * Extract a link url from an object (team Object, athlete Object etc)
   * @param  {Object} input     input Object
   * @param  {String} linkText link text for which link needs to be extracted
   *                           e.g. Clubhouse, Fixtures etc
   * @param  {String} device   link rel e.g. desktop, mobile etc
   * @param  {String} domain   domain rel e.g. espn.in, espncricinfo.com etc
   * @param  {String} sport    sports e.g. cricket, rugby etc
   * @param  {String} key      custom prop
   * @return {String}          extracted link url
   */
  extractUrl: function (input = {}, linkText = '', device = '', domain = '', sport = '', key = 'text' ) {
    // Get links else default to an empty Array
    const links = input.links || []
    let defaultLink = '#'
    let link = defaultLink
    // Iterate over links array
    for (var i = links.length - 1; i >= 0; i--) {
      // Get link object
      let linkObj = links[i] || {}
      //console.info('linkObj', linkObj)
      // Get link rel info
      let linkRel = linkObj.rel || []
      /**
       * Get the href only if - link text matches with the linkText arg &
       * this link has device in the rel list
       */
      if (linkObj[key] === linkText && linkRel.indexOf(device) > -1) {
        link = linkObj.href || defaultLink
        break
      }
    }
    if( sport === 'cricket' ) {
      link = common.buildURL(link, domain)
    } else {
      link = common.buildURL(link)
    }
    //console.info('link', link)
    return link
  },

  getUrlByRel: function (input = {}, rel = '', domain = '', sport = '') {
    // Get links else default to an empty Array
    const links = input.links || []
    let defaultLink = '#'
    let link = defaultLink
    // Iterate over links array
    for (var i = links.length - 1; i >= 0; i--) {
      // Get link object
      let linkObj = links[i] || {}
      //console.info('linkObj', linkObj)
      // Get link rel info
      let linkRel = linkObj.rel || []
      /**
       * Get the href only if - link text matches with the linkText arg &
       * this link has device in the rel list
       */
      if (linkRel.indexOf(rel) > -1) {
        link = linkObj.href || defaultLink
        break
      }
    }
    let relLink = ''
    if (sport == 'cricket') {
      relLink = common.buildURL(link, domain)
    } else {
      relLink = common.buildURL(link)
    }
    return relLink
  },

  /**
   * Get current game status
   * @param  {Object} sport       The current sport
   * @param  {Object} competition competition object describing game
   * @param  {Object} tzOptions   time zone options for formatting
   * @param {Boolean} timeValid   timeValid
   * @return {String}             Start time clock/Live/Final
   */
  getGameStatusText: function ( sport = '', competition = {}, tzOptions = {}, timeValid=true ) {
    let statusText = ''
    const state = selectn('status.type.state', competition) || 'pre'

    if( sport !== 'cricket' ) {
      switch (state) {
        case 'pre':
          statusText = dateFormat.convertDateTime(competition.date, tzOptions)
          break
        case 'in':
          statusText = selectn('status.displayClock', competition) || '0\''
          break
        case 'post':
          statusText = selectn('status.type.shortDetail', competition) || selectn('status.type.detail', competition) || selectn('status.type.description', competition) || state
          break
      }
    } else {

      // Hard coded cricket needs to be set to correct data driven!

      // For pre game state, get start time clock
      switch (state) {
        case 'pre':
          statusText = timeValid ? dateFormat.convertDateTime(competition.date, tzOptions) : ''
          break

        case 'in':
        case 'post':
          statusText = selectn('status.type.description', competition) || selectn('status.type.detail', competition) || selectn('status.type.shortDetail', competition) || state
          break
      }
    }
    return statusText
  },

  /**
   * [getGameBreakInfo gets games break detail]
   * @param  {[type]} state [state]
   * @return {[type]}       [string as in Lunch, Dinner etc]
   */
  getGameBreakInfo: function (state) {
    let breakInfo = state
    let breakState
    if(typeof breakInfo !== 'undefined') {

      breakInfo = breakInfo.toLowerCase()
      let sessionmap = {
        'current innings' : 'current',
        'drinks break' : 'drinks',
        'innings break' : 'innings',
        'lunch break' : 'lunch',
        'tea break' : 'tea',
        'dinner break' : 'dinner'
      }

      Object.keys(sessionmap).forEach(function ( value, index ) {
        if(index > 0) {
          if((breakInfo.indexOf(sessionmap[value]) > -1) && (breakInfo.indexOf('current') === -1)) {
            breakState = sessionmap[value]
          }
        }
      })
    }
    return breakState || ''
  },
  /**
   * Get state of a event/game
   * @param  {String} state state
   * @return {String}       pregame/live/final
   */
  getGameState: function (state) {
    const stateMap = {
      'pre' : 'pregame',
      'in' : 'live',
      'post' : 'final'
    }
    return stateMap[state] || 'pregame'
  },

  /**
   * Get CSS classname based on game state
   * @param  {String} state state
   * @return {String}       pre/live/post
   */
  getGameStateClass: function (state) {
    const stateMap = {
      'pregame' : 'pre',
      'pre' : 'pre',
      'in' : 'live',
      'live' : 'live',
      'post' : 'post',
      'final' : 'post'
    }
    return stateMap[state] || 'pre'
  },

  /**
   * Get class which will be applied for game strip to indicate winner
   * @param  {Array}  competitors competitors array from feed
   * @param  {String} sport sport
   * @param  {Boolean} teamFormat flag to return in team-[a|b]-winner format
   * @return {String}             home-winner or away-winner
   */
  getWinnerClass: function (competitors = [], sport = '', teamFormat) {
    let classString = ''
    for (var i = 0; i < competitors.length; i++) {
      let competitor = competitors[i] || {}
      // check for both string and boolean types of winner property
      if (competitor.winner === true || competitor.winner === 'true') {
        if(sport === 'cricket') {
          classString = competitor.homeAway
        } else if(teamFormat) {
          classString = (competitor.homeAway == 'home' ? 'team-a' : 'team-b') + '-winner'
        } else {
          classString = competitor.homeAway + '-winner'
        }
        // Break aftrer a winner is found
        break
      }
    }
    //console.info('classString', classString)
    return classString
  },

  /**
   * Get winner team which will be used to apply winner class on game strip
   * @param  {Array}  competitors competitors array from feed
   * @return {Object}             { home: true } or { away: true }
   */
  // TODO: remove `getWinnerClass()` Fn once `getWinner()` Fn is tested and approved
  getWinner: function (competitors = []) {
    let winner = {}
    for (var i = 0; i < competitors.length; i++) {
      let competitor = competitors[i] || {}
      // check for both string and boolean types of winner property
      if (competitor.winner === true || competitor.winner === 'true') {
        winner[competitor.homeAway] = true
        // Break aftrer a winner is found
        break
      }
    }

    return winner
  },

  /**
   * Get links for a game
   * @param  {Array}  links links (scoreboard/preview/highlghts etc)
   * @param  {String} sport sport
   * @param  {Object} edition   editionConfig
   * @param {Boolean} isHighlight flag if highlghts present
   * @param {String} gameState game status
   * @return {Array}        link objects, each with href, name and rel properties
   */
  getGameLinks: function (links = [], sport, edition = {}, isHighlight, gameState) {
    let gamePageLinks = []
    let constructHighlightUrl = ''
    let { userGeo = '', domain = '' } = edition
    for(let i = 0; i < links.length; i++) {
      const link = links[i] || {}
      if (link.text === 'WatchESPN' && userGeo !== 'us') {
        continue
      }
      const rel = link.rel || []
      let href = ''
      if (sport == 'cricket') {
        href = common.buildURL(link.href, domain)
      } else {
        href = common.buildURL(link.href)
      }

      const gameLinks = {
        href:href,
        text:link.text,
        rel:rel,
        trackingName: `&lpos=${sport}:scoreboard:${rel[2]}`
      }
      // Set react flag for game package pages
      if(common.isLinkReactEnabled(link.href)) {
        gameLinks.react = true
      }
      if( gamePageLinks.length >= 3 && sport != 'cricket' ) {
        return gamePageLinks
      }
      // Summary Link will be avaiable for pre, live and post games Link list, so added condition to pick Summary link to construct highlight link
      if(isHighlight && link.text === 'Summary') {
        constructHighlightUrl = common.constructUrl(href)
      }

      gamePageLinks.push(gameLinks)
    }

    if(isHighlight) {
      gamePageLinks.push({
        href: constructHighlightUrl,
        text: 'Highlights',
        react: true,
        trackingName: `&lpos=${sport}:scoreboard:highlights}`,
        rel: []
      })
    }
    if( sport === 'cricket' ) {
      let filterLinks = common.getFilterLinks(gameState, gamePageLinks)
      return filterLinks
    } else {
      return gamePageLinks
    }
  },


  getFilterLinks: function (state = '', links = []) {

    let results = []
    let gameLinkType = {
      pregame : [ 'Summary', 'Preview', 'Series Home' ],
      live : [ 'WatchESPN', 'Summary', 'Highlights','Scorecard', 'Report', 'Series Home' ],
      final : [ 'Scorecard', 'Highlights', 'Report', 'Summary', 'Series Home' ]
    }

    if( gameLinkType[state] ) {
      gameLinkType[state].forEach( function (key) {
        links.filter( function (item) {
          if (item.text === key) {
            if(state === 'live') {
              if (item.text === 'Summary') {
                item.text = 'Live'
              } else if(item.text === 'WatchESPN') {
                item.text = 'Watch'
              }
            }
            if( item.href ) {
              const isEspnCricinfoLegacy = (item.href.indexOf('espncricinfo.com') > -1 && /(\/content\/|\/engine\/)/.test(item.href)) ? true : false
              item.href = (isEspnCricinfoLegacy === true) ? item.href : (parseUri(item.href).relative || '')
            }
            results.push(item)
          }
        })

      })
    }
    results.splice(3)
    return results
  },
  /**
   * Get watch ESPN link
   * @param  {Array}  links links (summary/recap/watchESPN etc)
   * @param  {String} userGeo edition geo code
   * @return {Object} link object with href, name and rel properties
   * @param  {String} device   link rel e.g. desktop, mobile etc
   */
  getWatchESPNLink: function (links = [], userGeo = '', device = {}) {
    let watchESPNLink = {}
    //loops over the links array and pick only the WatchESPN, only for US geo users and only if device type is desktop
    for(let i = 0; i < links.length; i++) {
      const link = links[i] || {}
      if (link.text === 'WatchESPN' && userGeo === 'us' && device.desktop === true) {
        const rel = link.rel || []
        const href = common.buildURL(link.href)
        watchESPNLink = {
          href:href,
          text:link.text,
          rel:rel
        }
      }
    }

    return watchESPNLink
  },

  // Construct hightlights page url from summary url
  constructUrl: function (href) {
    let hightlightLink = ''
    hightlightLink = href.replace('/game', '/video')
    return hightlightLink
  },

  // TODO: Add more checks and conditions
  // presently only a palceholder used for game package links
  // on scoreboard
  isLinkReactEnabled: function (href) {
    return /game|scorecard|preview|report|video/i.test(href || '')
  },

  /**
   * For a sport, Should All Series option be shown in leagues dropdown?
   * @param  {String} sport name of sport
   * @return {Boolean}       true if allscores option should be shown else false
   */
  showAllScoresOption: function (sport) {
    // default return type
    let showAllScores = false
    // List of sports in which all series option is required
    var sportsAllscores = [ 'cricket', 'rugby' ]
    // If sport is found in array of sports (in which option should be shown)
    if (sportsAllscores.indexOf(sport) > -1) {
      showAllScores = true
    }
    return showAllScores
  },


  /**
   * Get active players for a live match
   * This is a wrapper function to get active players for a live game.
   * Specific players (e.g. batsman/bowlers in cricket) should be processed in
   * sport specific files.
   *
   * @param  {Array}  teams teams
   * @return {Array}      list of all active players.
   */
  getActivePlayers: function ( teams = []) {
    let activePlayers = []
    for (var i = 0; teams && i < teams.length; i++) {
      let team = teams[i]
      let isRosterPresent = team.hasOwnProperty('roster') && team.roster.length > 0
      if (isRosterPresent === false) {
        continue
      }
      let roster = team.roster
      roster.map(function ( player = {} ) {
        if (player.hasOwnProperty('active') && player.active === true) {
          activePlayers.push(player)
        }
      })
    }
    return activePlayers
  },


  /**
   * Returns a comparator function to compare 2 objects on a property name.
   * @param  {String} entityName property name on which 2 objects should be
   *                             compared
   * @return {Function}          comparator function
   *
   *@example
   * To sort person objects in persons array on age property, Use this:
   * persons.sort(compareEntities('age'))
   */
  compareEntities: function (entityName) {
    return function (p1, p2) {
      //console.info('p1,p2, entityName', p1,p2, entityName)

      // Return if property name is missing in both objects
      if (p1.hasOwnProperty(entityName) === false &&
          p2.hasOwnProperty(entityName) === false) {
        return 0
      }
      // default values
      let value1
      let value2
      // Try to compare the integer values else compare as is
      try{
        value1 = parseInt(p1[entityName], 10)
        value2 = parseInt(p2[entityName], 10)
      }
      catch(e) {
        value1 = p1[entityName]
        value2 = p2[entityName]
      }

      // return -1 if object p1 needs to be placed before p2
      if (value1 < value2) {
        return -1
      }
      // return +1 if object p1 needs to be placed after p2
      if (value1 > value2) {
        return 1
      }
      // Keep order as is
      return 0
    }
  },

  /**
   * Split score into runs and overs/target string
   * @param  {String} score Full score
   * @return {Array}        [ runs , overs/target ]
   */
  splitScore: function (score) {
    /**
     * Split score into runs and overs since the markup requires it
     */
    let runDetails = score
    let overDetails = ''
    // placeholder for split string
    let runs
    let overs

    // On which character score needs to be split?
    let splitOn = '('
    // If teamscore has ( n overs) text, split on (
    if (score.indexOf(splitOn) > -1) {
      // Extract overs/target components from score
      let splitScores = score.split('(')
      // Proceed only if array has 2 elememts score & overs/target
      if (splitScores.length === 2) {
        [ runs, overs ] = splitScores
        // Update final variables only if values are found
        if (runs && runs !== '') {
          runDetails = runs
        }
        if (overs && overs !== '') {
          // Prepend character on which score was split on earlier
          overDetails = `${splitOn}${overs}`
        }
      }
    }

    return [ runDetails, overDetails ]
  },

  /**
   * Get the markup of full score.
   * e.g. Returns 157/9 <span class='over'>(20/20 ov)</span>
   *
   * @param  {String}  score     score of a team
   * @param  {String}  className name of class that needs to be used to show overs
   * @param  {String}  elem      element p/span/div etc
   * @param  {Boolean} displayIndicator does * needs to be displayed if innings is current
   * @param  {Boolean} isBatting Is this team batting? If yes, display * after the score
   * @param  {Boolean} showTarget show target in score markup?
   * @param  {Boolean} isMobile - True if device is mobile
   * @return {String}           returns full markup.
   */
  getFullScoreMarkup: function (score = '', className = 'over', elem = 'span', displayIndicator = false, isBatting = false, showTarget = false, isMobile = false) {
    let scoreMarkup = score
    try{
      scoreMarkup = score.replace((/\([^)]+\)/gmi), function ( text ) {
        // Remove the target text from the string
        if (typeof text === 'string') {
          if (showTarget === false) {
            text = text.split(', target')[0]
          }
          text += text.lastIndexOf(')') > 0 ? '' : ')'
          if (isMobile && text.indexOf('target') !== -1) {
            text = text.replace('target', 'tgt')
          }
        }
        let sections = [ `<${elem} class='${className}'>${text}</${elem}>` ]
        // Prepend * after the score only if required and this team is currently batting, don't add for (f/o) text
        if (displayIndicator === true && isBatting === true && text !== '(f/o)') {
          sections.unshift('*')
        }
        return sections.join(' ') }
      )
    }
    catch(e) {
      //console.info('e', e)
    }
    return scoreMarkup
  },

  /**
   * Determine link (href) passed is external. Articles/ news feed item opens
   * up a new window and have icon for external links
   *
   * @param  {String}  href     href of an article
   * @param  {String}  domain     domain
   * @return {Boolean}
   */

  isLinkExternal: function (href, domain) {
    let isLinkExternal = false
    const hostReplaces = /www\.|-qa|qa\.|preview\./

    if (typeof domain === 'string') {
      domain = domain.replace(hostReplaces, '')
    }

    if(
      (href.startsWith('http') && href.indexOf('espn.go.com') === -1 && href.indexOf('en.espn.co.uk') === -1 && href.indexOf(domain) === -1)
        || href.indexOf('xgames') != -1
        || href.indexOf('espnfc.') != -1
        || href.indexOf('/espnw/') != -1
        || href.indexOf('thecricketmonthly.') != -1
    ) {
      isLinkExternal = true
    }

    return isLinkExternal
  },


  /**
   * Prepare commentary - API gives commentary in a object. Sort the objects
   * based on sortOrder key. Later, sort the array in descending order so that
   * latest ball commentary is on top.
   *
   * sortOrder is floating point number (eg. 1000.04),
   * so use another key (sortOrder converted to Integer) for sorting
   *
   * @param  {Object} commsMap commentary object as received from API
   * @return {Array}           Commentary ordered on sortOrder
   */
  prepareCommentary: function (commsMap = {}) {
    // Init default return value
    let sortedComms = []
    // name of key that needs to be looked up in object
    let lookup = 'sortOrder'
    // name of new key which will be used for sorting
    let sortKey = 'intSortOrder'

    // Iterate over all keys and push value to sortedComms
    for (let commId in commsMap) {
      // Continue to next key if it is not property of commsMap
      if (commsMap.hasOwnProperty(commId) === false) {
        continue
      }
      // Cache the object for reference
      let commDetails = commsMap[commId] || {}
      // Set a new property only if sortOrder value is a valid number
      if (commDetails.hasOwnProperty(lookup)) {
        // Value which will be used for sorting should be integer
        if (Number.isFinite(commDetails[lookup])) {
          // Ensure that sortOrder has 2 values after decimal point
          let val = commDetails[lookup].toFixed(2)
          //console.info('val', val)
          // Set new sort key = val multiplied by 100
          commDetails[sortKey] =  val * 100
          //console.info('commDetails.intSortOrder', commDetails[sortKey])
        }
      }
      sortedComms.push(commDetails)
    }
    // Sort the array on sortKey(new key set as above)
    sortedComms.sort(common.compareEntities(sortKey))
    // compareEntities sorts in ascending order , so reverse it
    sortedComms.reverse()
    //console.info('sortedComms', sortedComms)
    return sortedComms
  },

  /**
   * Create and normalize query string object
   * @param {Object} edition   edition
   * @param {Object} params    params
   * @param {Object} location  locations
   * @param {Object} appProps  appProps
   * @return {Object} query object
   */
  getQuery: function (edition, params, location, appProps) {
    let query = location.query || qs.parse(location.search)
    if (!query.device && edition.isEspnCricinfo && params.webview === 'webview' && selectn('device.featurephone', appProps)) {
      query.device = 'featurephone'
    }

    return query
  },

  getLeagueLink: function (links = [], domain) {
    let leagueLink = ''
    links.map( (link = {}) => {
      if(link.text && link.text.indexOf('Series Home') !== -1) {
        leagueLink = link.href
      }
    })
    return common.buildURL(leagueLink, domain)
  },
  getNoContentText:  function (pageType, edition) {
    return (pageType === 'team') ? translate(edition, 'content.unavailable.clubhouse') : ''
  }

// Closing tag of common module
}

/**
 * Parser functions for leagues dropdown
 * @type {Object}
 */
const leagues = {

  /**
   * process data from APIs
   * @param  {Object} res         API response
   * @param  {Object} edition     edition config
   * @param  {Object} params      router params
   * @return {Object}             league items, current league
   */
  processData: function (res, edition = {}, params = {}) {
    const { pageType } = params
    const data = selectn('data.leagues', res)
    // Default return types
    let processedData = {
      items : [],
      currentLeague : {}
    }
    let filterByStandings

    // If response or params are empty, return default data
    if (typeof data === 'undefined' ||
         (params !== null &&
          typeof params !== 'undefined' &&
          Object.keys(params).length === 0)) {
      return processedData
    }

    if ((/table|standings/gi).test(pageType)) {
      filterByStandings = true
    }

    // Get all leagues
    processedData.items = leagues.get(data, filterByStandings)
    //const items = leagues.filterLeagues(res.data.leagues, params)

    // Upon validation, append All Series options to league
    leagues.appendAllScoresToLeagues( edition, processedData.items, params.pageType, params.sport)

    processedData.currentLeague = leagues.getCurrentLeague(data, params)

    //console.info('items, currentLeague', items, currentLeague)
    return processedData
  },


  /**
   * Get all leagues
   * @param  {Array}  leagues leagues array
   * @param {Boolean} filterByStandings flag if leagues to be filtered by standings
   * @return {Array}          array of league objexts. Each league object has
   *                                id, name amd hasStandings boolean
   */
  get: function ( leagues = [], filterByStandings) {
    let allLeagues = []
    leagues.map((league) => {
      if (filterByStandings === true && league.hasStandings === false) {
        return
      }

      let name = league.name

      if(filterByStandings && league.abbreviation) {
        name = league.abbreviation
      }

      if(league.id) {
        allLeagues.push({
          id: league.id,
          name: name,
          text: name,
          hasStandings: selectn('season.type.hasStandings', league),
          slug: league.slug,
          trackingName: common.createTrackingName([ league.id, 'standings', 'filter', 'league' ])
        })
      }
    })
    return allLeagues
  },


  /**
   * Append All Series option to leagues array.
   * Append this option only on scoreboard page and showAllScores is true.
   * Before appending, make sure it's not present already.
   * @param  {Object}  	edition  Editions Object
   * @param  {Array}  	items    list of leagues
   * @param  {String} 	pageType page type
   * @param  {String} 	sport    name of sport
   * @return {undefined}          undefined
   */
  appendAllScoresToLeagues: function ( edition = {}, items = [], pageType = '' , sport = '' ) {
    //console.info('appendAllScoresToLeagues', items, pageType, sport)
    let showAllScores = common.showAllScoresOption(sport)
    // Append all series only on scoreboard page and showAllScores is true
    if (/scoreboard|scores/.test(pageType) && showAllScores) {
      // Append only if all_series is not already appended and it's not undefined
      let firstItemId = selectn('[0].id', items)
      //console.info('firstItemId', firstItemId)

      let defaultKey = 'dropdown.leagues.default.' + sport
      let leagueDesc = translate(edition, defaultKey )
      if ( leagueDesc === defaultKey ) {
        leagueDesc = translate( edition, 'dropdown.leagues.default.sport' )
      }

      if (firstItemId !== 'all_series' && firstItemId !== 'undefined') {
        items.unshift({
          'href' : '/page/scoreboard',
          'text' : leagueDesc,
          'name' : leagueDesc,
          'abbr' : leagueDesc,
          'id' : ''
        })
      }
    }
  },

  /**
   * get list of valid leagues
   * @param  {Array} leagueItems - raw data of list of leagues
   * @return {Array}             - valid leagues
   */
  getLeaguesWithStandings: function (leagueItems) {
    let filterItems = []

    try{
      for(let item of leagueItems) {
        // filter league with stansings
        if(selectn('season.type.hasStandings', item) === 'true') {
          filterItems
            .push({
              id : item.id,
              name : item.name,
              text : item.name,
              trackingName: `&lpos=${item.id}:standings:filter:league`
            })
        }
      }

      return filterItems
    } catch(err) {
      return []
    }
  },


  /**
   * get current league object
   * @param  {Array} leagues  - list of valid leagues
   * @param  {Object} params  - router params
   * @return {Object}          league object
   */
  getCurrentLeague: function (leagues = [], params = {}) {
    const { league : leagueId = '', slug = '' } = params
    let leagueObj = {}

    // get current league details from the leagues list
    for(let league of leagues) {
      // match with league id
      if(league.id === leagueId) {
        leagueObj = {
          id : league.id,
          name : league.name,
          abbr : league.abbreviation || league.name,
          slug : league.slug
        }
      }
    }

    // fallback current league to params if not available in list
    if (Object.keys(leagueObj).length === 0) {
      leagueObj = {
        id : leagueId,
        slug
      }
    }

    return leagueObj
  },

  /**
   * Method to organize search result response
   * @param {Object} res       response
   * @param {Object} domain   domain object
   * @param {Object} appProps  appProps
   * @return {Object} search results object
  */
  searchLeagueResults:  function (res, domain) {
    let status = selectn('status', res)
    let resultObj = {}
    let deviceRel  = 'desktop'
    let leagueRel = 'serieshome'
    let defaultImage = 'http://a.espncdn.com/combiner/i?img=/redesign/assets/img/icons/espn-icon-cricket.png'
    defaultImage = common.resizeCombinerLogo(defaultImage, { w:30, h:30 } )
    if(res && res.status === 200) {
      let data = selectn('data.items', res) || []

      let dataLen = data.length || 0
      let totalRecords = selectn('data.count', res) || 0
      totalRecords = parseInt(totalRecords, 10)
      let resultsArr = []
      let message = ''
      let href = ''
      if(totalRecords > 0) {
        for(let i=0; i < dataLen; i++) {
          let item = data[i] !== 'undefined' ? data[i] : undefined

          if(item) {
            let id = selectn('id', item) || 0
            let type = ( selectn('type', item) === 'league' ) ? 'sport' : selectn('type', item)
            let links = selectn('links', item) || []

            if(links.length > 0) {
              links.forEach(function (l) {
                if(l.rel && l.rel.indexOf(leagueRel) > -1 && l.rel.indexOf(deviceRel) > -1) {
                  href = l.href
                }
              })
            }
            let imgs = selectn('logos', item) || selectn('assets', item) || []
            let img = defaultImage

            if(imgs.length > 0) {
              imgs.forEach( function (i) {
                if(i.rel && i.rel.indexOf('default') > -1) {
                  img = i.href && common.resizeCombinerLogo(i.href, { w:30, h:30 } )
                } else {
                  img = i.href
                }
              })
            }
            if(id) {
              let resultObj = {
                id: id,
                text: item.displayName || '',
                href: common.buildURL(href,domain),
                img: img,
                label: type
              }
              resultsArr.push(resultObj)
            }
          }
        }
      } else {
        message = 'No results found.'
      }
      resultObj = {
        totalRecords: totalRecords,
        results: resultsArr,
        status: status,
        message: message
      }
    }else{
      resultObj = {
        status: status,
        error: selectn('statusText', res)
      }
    }
    return resultObj
  }
}

export { common, leagues }

export default {
  common,
  leagues
}



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/common.js